// Puck data ⇄ PageBlock[] mapping (MOD-14 / Blok F).
//
// Puck's own data format is NEVER persisted — the BE `{ blockType, props }`
// array is the source-of-truth. On load we map the persisted blocks into
// Puck's `{ root, content }` shape; on save we map Puck's content back into
// blocks, stripping Puck-internal keys (`id`). Kept free of any `@puckeditor/core`
// import so it stays trivially unit-testable and out of the main bundle.

import { nanoid } from 'nanoid';
import { isBlockType, type PageBlock, type PersistableBlockType } from './blockTypes';

/** Minimal shape of a Puck content item — we only touch `type` + `props`. */
export interface PuckContentItem {
  type: string;
  props: Record<string, unknown>;
}

export interface PuckData {
  root: { props?: Record<string, unknown> };
  content: PuckContentItem[];
}

/** Keys Puck injects into every component's props that must not be persisted. */
const PUCK_INTERNAL_KEYS = ['id', 'editMode', 'puck'] as const;

function stripInternal(props: Record<string, unknown>): Record<string, unknown> {
  const next: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    if ((PUCK_INTERNAL_KEYS as readonly string[]).includes(key)) continue;
    next[key] = value;
  }
  return next;
}

/** Load: persisted blocks → Puck editor data. */
export function blocksToPuckData(blocks: PageBlock[]): PuckData {
  return {
    root: {},
    content: blocks.map((block) => ({
      type: block.blockType,
      // Puck requires a unique, stable `id` per content item.
      props: { ...block.props, id: `${block.blockType}-${nanoid(8)}` },
    })),
  };
}

/**
 * Save: Puck editor data → persisted blocks. Bilinmeyen tipler düşer; ama runtime
 * `cmp_` component blokları `isBlockType` sayesinde KORUNUR (R-3 round-trip).
 */
export function puckDataToBlocks(data: PuckData): PageBlock[] {
  return (data.content ?? [])
    .filter((item): item is PuckContentItem => isBlockType(item.type))
    .map((item) => ({
      blockType: item.type as PersistableBlockType,
      props: stripInternal(item.props ?? {}),
    }));
}
